import { useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Upload, Loader2 } from "lucide-react";

// Reusable image uploader — sends multipart/form-data to /api/staff/upload
// via the axios instance (bearer token attached by interceptor).
// Calls onUploaded(url) with the resulting URL (e.g. "/api/files/tbb-barracks/uploads/...").

export default function ImageUploader({ onUploaded, label = "Upload", testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/staff/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.url);
      toast.success("Uploaded");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <label
      data-testid={testid || "image-uploader"}
      className={`inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-wider px-3 py-2 rounded border border-[hsl(var(--rule))] cursor-pointer ${
        busy ? "opacity-60" : "hover:border-[hsl(var(--brass))]"
      }`}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
      {busy ? "Uploading..." : label}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
        className="hidden"
        onChange={handle}
        disabled={busy}
      />
    </label>
  );
}
